import { observer } from "mobx-react-lite";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { useCompanyStore } from "../hooks";
import { getLastDetailsStep } from "../utils/company.steps";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const CompanySetupFooter = observer(
  ({
    compact,
    isDialog,
    canContinueStep,
    nextStep,
    prevStep,
    handleSaveAndExit,
    handleReview,
    handleStart,
    isCompletedSetup,
    hasChanges,
    canSaveAndExit,
    summaryStep = 6,
    showSystemSettings = false,
    showGhgBaseline = false,
  }) => {
    const { t } = useTran();
    const { step } = useCompanyStore();
    const { user } = useAuthStore();
    const permissions = user?.permissions || [];
    const canShowSaveAndExit = Boolean(
      canSaveAndExit ?? (isCompletedSetup && hasChanges),
    );
    const writeAction = isCompletedSetup ? "update" : "create";
    const bypassPermissionCheck = isDialog || !isCompletedSetup;
    const isSummaryStep = step === summaryStep;
    // Gets Review/Continue instead of Next.
    const lastDetailsStep = getLastDetailsStep(showGhgBaseline);
    const isIntermediateStep = step < summaryStep && step !== lastDetailsStep;
    const isFirstTimeSetup = !isCompletedSetup;

    return (
      <div className="sticky bottom-0 left-0 right-0 mt-6 flex flex-col gap-3 border-t border-border pt-4 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={prevStep}
          disabled={step === 1}
          className="w-full sm:w-auto"
        >
          {t("Back")}
        </Button>

        {isIntermediateStep && !compact ? (
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            <Can
              resource="company_information"
              action={writeAction}
              userPermissions={permissions}
              defaultView={bypassPermissionCheck}
            >
              {canShowSaveAndExit ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveAndExit}
                  className="w-full sm:w-auto"
                >
                  {t("save_exit")}
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={nextStep}
                disabled={!canContinueStep}
                className="w-full sm:w-auto"
              >
                {t("continue")}
              </Button>
            </Can>
          </div>
        ) : null}

        {isIntermediateStep && compact ? (
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            <Can
              resource="company_information"
              action={writeAction}
              userPermissions={permissions}
              defaultView={bypassPermissionCheck}
            >
              {canShowSaveAndExit ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveAndExit}
                  className="w-full sm:w-auto"
                >
                  {t("save_exit")}
                </Button>
              ) : null}
              <Button
                type="button"
                onClick={nextStep}
                disabled={!canContinueStep}
                className="w-full sm:w-auto"
              >
                {t("continue")}
              </Button>
            </Can>
          </div>
        ) : null}

        {step === lastDetailsStep ? (
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            {compact && isFirstTimeSetup ? (
              <Can
                resource="company_information"
                action={writeAction}
                userPermissions={permissions}
                defaultView={bypassPermissionCheck}
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveAndExit}
                  className="w-full sm:w-auto"
                >
                  {t("skip_for_now")}
                </Button>
              </Can>
            ) : null}
            {canShowSaveAndExit ? (
              <Can
                resource="company_information"
                action={writeAction}
                userPermissions={permissions}
                defaultView={bypassPermissionCheck}
              >
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSaveAndExit}
                  className="w-full sm:w-auto"
                >
                  {t("save_exit")}
                </Button>
              </Can>
            ) : null}
            <Can
              resource="company_information"
              action="view"
              userPermissions={permissions}
              defaultView={bypassPermissionCheck}
            >
              <Button
                type="button"
                onClick={showSystemSettings ? nextStep : handleReview}
                disabled={!canContinueStep}
                className="w-full sm:w-auto"
              >
                {showSystemSettings ? t("Continue") : t("review")}
              </Button>
            </Can>
          </div>
        ) : null}

        {isSummaryStep ? (
          <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center sm:justify-end">
            {compact ? (
              <Can
                resource="company_information"
                action={writeAction}
                userPermissions={permissions}
                defaultView={bypassPermissionCheck}
              >
                <Button
                  type="button"
                  onClick={handleStart}
                  className="w-full sm:w-auto"
                >
                  {isDialog
                    ? isCompletedSetup
                      ? t("Update")
                      : t("save_exit")
                    : t("lets_start")}
                </Button>
              </Can>
            ) : (
              <>
                {canShowSaveAndExit || isFirstTimeSetup ? (
                  <Can
                    resource="company_information"
                    action={writeAction}
                    userPermissions={permissions}
                    defaultView={bypassPermissionCheck}
                  >
                    <Button
                      type="button"
                      onClick={handleSaveAndExit}
                      className="w-full sm:w-auto"
                    >
                      {t("save_exit")}
                    </Button>
                  </Can>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </div>
    );
  },
);

export default CompanySetupFooter;
